import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth';
import { useLocker } from '../features/locker';
import { useToast } from '../features/toast';
import { auth } from '../services/firebase';
import FreeShippingProgress from '../components/FreeShippingProgress';
import {
  aggregateLockerShipments,
} from '../utils/shippingUtils';
import Seo from '../shared/Seo';
import { getMachines } from '../services/api';
import { redeemCollectionReward } from '../services/api';
import { useCheckout } from '../features/checkout';

const FALLBACK_IMAGE = '/assets/ponpon-logo.svg';

const LockerPage = () => {
  const { user, login } = useAuth();
  const { startShippingFlow, openLockerFullModal } = useLocker();
  const { queueTokenGain, showShippingRewardModal } = useCheckout();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isAuthenticated = Boolean(user);
  const [freeMachines, setFreeMachines] = useState([]);
  const [redeemingCollectionId, setRedeemingCollectionId] = useState(null);

  useEffect(() => {
    let active = true;
    if (!isAuthenticated) {
      setFreeMachines([]);
      return () => {
        active = false;
      };
    }
    getMachines()
      .then((machines) => {
        if (!active) return;
        const nextMachines = Array.isArray(machines)
          ? machines.filter((machine) => Number(machine?.tokenCost) === 0)
          : [];
        if (nextMachines.length === 0) return;
        setFreeMachines((current) => {
          const unchanged = current.length === nextMachines.length &&
            current.every((machine, index) => machine?.id === nextMachines[index]?.id);
          return unchanged ? current : nextMachines;
        });
      })
      .catch(() => {
        // Keep the Collection empty when machine data is unavailable.
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated]);

  const lockerItems = useMemo(() => {
    const source = Array.isArray(user?.locker)
      ? user.locker
      : Array.isArray(user?.history)
      ? user.history
      : [];
    if (!Array.isArray(source)) {
      return [];
    }
    return [...source]
      .sort((a, b) => {
        const timeA = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });
  }, [user?.locker, user?.history]);
  const pendingPrize = user?.pendingPrize || null;
  const collectionItems = useMemo(() => {
    const source = Array.isArray(user?.collection) ? user.collection : [];
    return [...source].sort((a, b) => {
      const timeA = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA;
    });
  }, [user?.collection]);
  const collectionSets = useMemo(() => {
    const ownedByKey = new Map(
      collectionItems.map((entry) => [
        `${entry?.collectionId || entry?.machineId}:${entry?.prizeId || entry?.prize?.id}`,
        entry,
      ]),
    );
    const rewardedCollections = new Set(
      (Array.isArray(user?.collectionRewards) ? user.collectionRewards : [])
        .map((reward) => reward?.collectionId)
        .filter(Boolean),
    );
    const setsById = new Map();
    freeMachines.forEach((machine) => {
      const collectionId = machine.collectionId || machine.id;
      if (!setsById.has(collectionId)) {
        setsById.set(collectionId, {
          id: collectionId,
          name: machine.collectionName || machine.name || machine.primary?.name || 'Free Play Collection',
          prizesById: new Map(),
        });
      }
      const set = setsById.get(collectionId);
      (Array.isArray(machine?.prizePool) ? machine.prizePool : []).forEach((prize) => {
        if (prize?.id && !set.prizesById.has(prize.id)) {
          set.prizesById.set(prize.id, prize);
        }
      });
    });
    return [...setsById.values()].map((set) => {
      const stickers = [...set.prizesById.values()].map((prize) => ({
        prize,
        ownedEntry: ownedByKey.get(`${set.id}:${prize.id}`) || null,
      }));
      return {
        id: set.id,
        name: set.name,
        stickers,
        owned: stickers.filter((sticker) => sticker.ownedEntry).length,
        rewarded: rewardedCollections.has(set.id),
        completed: stickers.length > 0 &&
          stickers.every((sticker) => Boolean(sticker.ownedEntry)),
      };
    });
  }, [collectionItems, freeMachines, user?.collectionRewards]);

  const handleRedeemCollection = async (collectionId) => {
    if (!collectionId || redeemingCollectionId) return;
    setRedeemingCollectionId(collectionId);
    try {
      const result = await redeemCollectionReward(collectionId);
      const tokensAwarded = Number(result?.reward?.tokensAwarded) || 1;
      if (result?.user && typeof login === 'function') {
        login(result.user);
      }
      queueTokenGain(tokensAwarded);
      showShippingRewardModal({
        tokensAwarded,
        tokenBalance: result?.user?.tokens,
        shipmentId: collectionId,
        rewardKind: 'collection',
      });
    } catch (error) {
      showToast({
        message: error.response?.data?.error || 'Unable to redeem this Collection reward.',
        tone: 'error',
      });
    } finally {
      setRedeemingCollectionId(null);
    }
  };
  
  // CHANGED: combine lockerItems with pendingPrize for display, with pendingPrize at the top
  const displayItems = useMemo(() => {
    const items = [];
    if (pendingPrize) {
      items.push({
        ...pendingPrize,
        isPending: true,
        timestamp: pendingPrize?.timestamp || new Date().toISOString(),
      });
    }
    items.push(...lockerItems);
    return items;
  }, [pendingPrize, lockerItems]);
  const canShip = lockerItems.length > 0;
  const currentCount = lockerItems.length;
  const handleShipClick = () => {
    if (canShip) {
      startShippingFlow('manual');
    }
  };
  const handleOpenLockerFullModal = () => {
    // CHANGED: call openLockerFullModal to trigger LockerFullModal display
    openLockerFullModal();
  };
  const shippingGroups = useMemo(
    () => aggregateLockerShipments(user?.lockerShipments),
    [user?.lockerShipments],
  );
  const [expandedShipments, setExpandedShipments] = useState(() => new Set());
  const toggleShipmentExpansion = (groupId) => {
    setExpandedShipments((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // CHANGED: keep page visible; use toast to block actions when needed

  return (
    <div className="flex flex-col gap-8">
      <Seo
        title="Locker | PonPon!"
        description="Use the PonPon Locker to store every prize, expand capacity when needed, review spin history, and trigger a free shipment once your slots are full."
        canonical="https://ponpon.app/locker"
        noindex={true}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
            Your Locker
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 rounded-full border border-ponpon-navy/15 bg-white px-5 py-2 text-sm font-semibold text-ponpon-navy/70 transition hover:-translate-y-0.5 hover:border-ponpon-coral/40 hover:text-ponpon-coral focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/50"
          >
            <span aria-hidden="true">←</span>
            Back to homepage
          </button>
          {/* CHANGED: add entry point to Locker Showcase builder */}
          <button
            type="button"
            onClick={() => navigate('/locker/showcase')}
            className="inline-flex items-center gap-2 rounded-full border border-ponpon-navy/15 bg-white px-5 py-2 text-sm font-semibold text-ponpon-navy/80 transition hover:-translate-y-0.5 hover:border-ponpon-coral/40 hover:text-ponpon-coral focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/50"
          >
            PonDisplay
          </button>
          {canShip ? (
            <button
              type="button"
              onClick={async () => {
                if (!isAuthenticated) {
                  showToast({ message: 'Sign in to ship your Locker items.', tone: 'info' });
                  return;
                }
                // CHANGED: use Firebase auth.currentUser.emailVerified for consistency (Google users are auto-verified)
                const currentUser = auth.currentUser;
                if (!currentUser) {
                  showToast({ message: 'Sign in to ship your Locker items.', tone: 'info' });
                  return;
                }
                try { await currentUser.reload(); } catch (_) {}
                if (!auth.currentUser?.emailVerified) {
                  showToast({ message: 'Verify your email before shipping your Locker.', tone: 'info' });
                  return;
                }
                handleShipClick();
              }}
              className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
            >
              Ship My Locker
            </button>
          ) : null}
        </div>
      </div>

      {isAuthenticated ? (
        <div className="flex w-full flex-col gap-3">
          <p className="flex items-center gap-2 text-lg font-semibold text-ponpon-navy">
            <span>Collect 8</span>
            <img
              src="/assets/yellow_gacha.png"
              alt="Gachapon capsule"
              className="h-6 w-6 object-contain"
              loading="lazy"
              decoding="async"
            />
            <span>to unlock free shipping!</span>
          </p>
          <FreeShippingProgress currentCount={currentCount} />
        </div>
      ) : null}

      {pendingPrize ? (
        <div className="rounded-3xl border border-ponpon-coral/30 bg-ponpon-coral/5 p-4 text-sm text-ponpon-coral">
          Your locker is full.{' '}
          <button
            type="button"
            onClick={handleOpenLockerFullModal}
            className="font-semibold underline underline-offset-2 hover:text-ponpon-navy transition-colors"
          >
            Ship or expand
          </button>{' '}
          to claim your pending prize.
        </div>
      ) : null}

      {isAuthenticated ? (
        <section className="flex flex-col gap-4" aria-labelledby="collection-heading">
          <header>
            <h2 id="collection-heading" className="font-display text-2xl font-semibold text-ponpon-navy">
              Collection
            </h2>
            <p className="mt-1 text-sm text-ponpon-navy/60">
              Digital stickers from free plays. Collect to earn tokens.
            </p>
          </header>
          {collectionSets.length > 0 ? (
            <div className="flex flex-col gap-6">
              {collectionSets.map((set) => (
                <section key={set.id} className="rounded-3xl border border-ponpon-gold/25 bg-white/70 p-4 sm:p-5">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="text-lg font-semibold text-ponpon-navy">{set.name}</h3>
                      <p className="text-sm text-ponpon-navy/55">{set.owned}/{set.stickers.length} collected</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRedeemCollection(set.id)}
                      disabled={!set.completed || set.rewarded || Boolean(redeemingCollectionId)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                        set.rewarded
                          ? 'bg-ponpon-gold text-ponpon-navy'
                          : set.completed
                          ? 'bg-ponpon-coral text-white shadow-sm hover:bg-ponpon-coral/90'
                          : 'cursor-not-allowed bg-ponpon-navy/5 text-ponpon-navy/55'
                      }`}
                    >
                      {set.rewarded
                        ? 'Complete · 1 Token Earned'
                        : redeemingCollectionId === set.id
                        ? 'Redeeming...'
                        : 'Complete set · Earn 1 Token'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                    {set.stickers.map(({ prize, ownedEntry }) => {
                      const isOwned = Boolean(ownedEntry);
                      const prizeName = prize?.name || 'Mystery sticker';
                      const prizeImg = typeof prize?.imageUrl === 'string' && prize.imageUrl.trim().length > 0
                        ? prize.imageUrl.trim()
                        : FALLBACK_IMAGE;
                      return (
                        <article
                          key={prize?.id || prizeName}
                          className={`relative flex h-full flex-col gap-3 rounded-3xl border p-4 shadow-sm ${isOwned ? 'border-ponpon-gold/30 bg-gradient-to-br from-ponpon-gold/10 to-white' : 'border-ponpon-navy/10 bg-ponpon-navy/[0.03]'}`}
                        >
                          <div className="flex min-h-6 w-full items-center justify-end">
                            <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${isOwned ? 'bg-ponpon-gold text-ponpon-navy' : 'bg-ponpon-navy/10 text-ponpon-navy/45'}`}>
                              {isOwned ? 'Collected' : 'Locked'}
                            </span>
                          </div>
                          <div className="flex h-32 w-full shrink-0 items-center justify-center overflow-hidden rounded-2xl p-2 sm:h-40">
                            <img
                              src={prizeImg}
                              alt={isOwned ? prizeName : 'Uncollected sticker silhouette'}
                              className={`h-full w-full object-contain ${isOwned ? 'drop-shadow-[0_5px_7px_rgba(30,35,51,0.2)]' : 'brightness-0 opacity-35 drop-shadow-[0_4px_6px_rgba(30,35,51,0.18)]'}`}
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                          <div className="space-y-1">
                            <h4 className={`font-semibold ${isOwned ? 'text-ponpon-navy' : 'text-ponpon-navy/40'}`}>
                              {isOwned ? prizeName : '???'}
                            </h4>
                            <p className="text-[11px] font-medium uppercase tracking-wide text-ponpon-navy/45">
                              {isOwned ? 'Sticker unlocked' : 'Not collected'}
                            </p>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-ponpon-gold/40 bg-ponpon-gold/5 p-8 text-center text-sm text-ponpon-navy/60">
              No digital sticker collections are available yet.
            </div>
          )}
        </section>
      ) : null}

      <section className="flex flex-col gap-4" aria-labelledby="physical-locker-heading">
        <header>
          <h2 id="physical-locker-heading" className="font-display text-2xl font-semibold text-ponpon-navy">
            Locker
          </h2>
          <p className="mt-1 text-sm text-ponpon-navy/60">Physical prizes that can be shipped to you.</p>
        </header>

      {displayItems.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {displayItems.map((entry) => {
            const isPending = entry?.isPending || false;
            const prizeName = entry?.prize?.name || entry?.name || 'Mystery prize';
            const prizeImg =
              typeof entry?.prize?.imageUrl === 'string' && entry.prize.imageUrl.trim().length > 0
                ? entry.prize.imageUrl.trim()
                : typeof entry?.imageUrl === 'string' && entry.imageUrl.trim().length > 0
                ? entry.imageUrl.trim()
                : FALLBACK_IMAGE;
            const machineName = entry?.machineName || entry?.prize?.machineName || 'PonPon Machine';
            // CHANGED: Only keep calendar date (no time) for Locker items
            const timestamp = entry?.timestamp ? new Date(entry.timestamp) : null;
            const formattedDate = Number.isFinite(timestamp?.getTime())
              ? new Intl.DateTimeFormat(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }).format(timestamp)
              : 'Just now';

            return (
              <article
                key={entry?.id || `${entry?.prize?.id || entry?.id || 'pending'}-${entry?.timestamp || 'pending'}`}
                className={`relative flex flex-col gap-4 rounded-3xl border p-5 shadow-sm transition ${
                  isPending
                    ? 'border-ponpon-coral/50 bg-gradient-to-br from-ponpon-coral/10 to-white/95 ring-2 ring-ponpon-coral/20'
                    : 'border-white/70 bg-white/95 hover:-translate-y-1 hover:shadow-lg'
                }`}
              >
                {isPending ? (
                  <span className="absolute right-4 top-4 rounded-full bg-ponpon-coral px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm">
                    Pending
                  </span>
                ) : null}
                <div className="flex items-center justify-center rounded-2xl p-5">
                  <img
                    src={prizeImg}
                    alt={prizeName}
                    className="max-h-28 w-auto object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold text-ponpon-navy">{prizeName}</h2>
                  <p className="text-xs font-medium uppercase tracking-wide text-ponpon-navy/55">
                    {machineName}
                  </p>
                  {/* CHANGED: Show only date (no time) in Locker item card */}
                  <p className={`text-xs ${isPending ? 'text-ponpon-coral/70 font-semibold' : 'text-ponpon-navy/60'}`}>
                    {isPending ? 'Waiting for locker space' : formattedDate}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-ponpon-navy/20 bg-white/90 p-10 text-center text-sm text-ponpon-navy/65">
          Spin a token machine to start filling your physical Locker.
        </div>
      )}
      </section>

      {shippingGroups.length ? (
        <section className="flex flex-col gap-4">
          <header>
            <h2 className="text-lg font-semibold text-ponpon-navy">Order Status</h2>
          </header>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-2">
            {shippingGroups.map((group) => {
              // CHANGED: Simplified shipped card — remove fee/status text
              const isExpanded = expandedShipments.has(group.id);
              const displayItems = isExpanded ? group.items : group.items.slice(0, 2);
              const hiddenCount = Math.max(group.items.length - displayItems.length, 0);
              const canCollapse = isExpanded && group.items.length > 2;

              return (
                <article
                  key={group.id}
                  className="flex flex-col gap-4 rounded-3xl border border-white/70 bg-white/90 p-5 shadow-sm ring-1 ring-ponpon-navy/5"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-2">
                      <span className="inline-flex items-center rounded-full bg-ponpon-navy/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-ponpon-navy">
                        {/* CHANGED: Badge label from SHIPPING to SHIPPED */}
                        SHIPPED
                      </span>
                      <div className="space-y-1">
                        {group.shipmentId || group.id ? (
                          <p className="text-lg font-semibold text-ponpon-navy">
                            {group.shipmentId || group.id}
                          </p>
                        ) : null}
                        {group.trackingNumber ? (
                          <p className="text-lg font-semibold text-ponpon-navy">
                            Tracking #{group.trackingNumber}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {/* CHANGED: Only show count when more than 1 item, format as "[n] total" */}
                    {group.itemCount > 1 ? (
                      <p className="self-end text-xs font-semibold uppercase tracking-wide text-ponpon-navy/60 sm:self-auto">
                        {group.itemCount} total
                      </p>
                    ) : null}
                  </div>
                  <div
                    id={`shipment-items-${group.id}`}
                    className="flex flex-wrap gap-3 border-t border-ponpon-navy/10 pt-4"
                  >
                    {displayItems.map((item) => {
                      const prizeName = item?.prize?.name || 'Mystery prize';
                      const prizeImg =
                        typeof item?.prize?.imageUrl === 'string' && item.prize.imageUrl.trim().length > 0
                          ? item.prize.imageUrl.trim()
                          : FALLBACK_IMAGE;

                      return (
                        <div
                          key={item?.id || item?.prize?.id || `${group.id}-${prizeName}`}
                          className="flex items-center gap-2 rounded-2xl border border-ponpon-navy/10 bg-white/75 px-3 py-2"
                        >
                          <img
                            src={prizeImg}
                            alt={prizeName}
                            className="h-10 w-10 rounded-xl object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="text-xs">
                            <p className="font-semibold text-ponpon-navy">{prizeName}</p>
                            <p className="text-[10px] uppercase tracking-wide text-ponpon-navy/55">
                              {item?.machineName || item?.prize?.machineName || 'PonPon Machine'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {hiddenCount && !isExpanded ? (
                      <button
                        type="button"
                        onClick={() => toggleShipmentExpansion(group.id)}
                        className="inline-flex items-center rounded-2xl bg-ponpon-navy/5 px-3 py-2 text-xs font-semibold text-ponpon-navy/70 transition hover:-translate-y-0.5 hover:bg-ponpon-coral/10 hover:text-ponpon-coral"
                        aria-label={`View ${hiddenCount} more items for this shipment`}
                      >
                        +{hiddenCount} more
                      </button>
                    ) : null}
                  </div>
                  {canCollapse ? (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => toggleShipmentExpansion(group.id)}
                        className="text-xs font-semibold uppercase tracking-wide text-ponpon-navy/70 underline-offset-4 hover:text-ponpon-coral hover:underline"
                      >
                        Hide
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default LockerPage;
